import { Clock, MonitorSmartphone, Info } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { getByCategory, detailPath } from "@/lib/programsData";

const InternshipCard = ({ p, i }) => (
  <StaggerItem className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-7">
    <span className="mb-4 w-fit rounded-full bg-orange px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
      Free · Selected Candidates
    </span>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange">
      <Clock className="h-3.5 w-3.5" /> {p.duration}
    </div>
    <h3 className="mt-3 font-heading text-2xl leading-snug text-white">{p.name}</h3>
    <p className="mt-3 text-sm leading-relaxed text-white/65">{p.homeSummary}</p>
    <div className="mt-5 flex flex-wrap gap-2 text-xs text-white/60">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1"><MonitorSmartphone className="h-3.5 w-3.5" /> {p.mode}</span>
    </div>
    <div className="mt-5 border-t border-white/10 pt-5">
      <div className="font-heading text-xl text-white">{p.fee}</div>
      <div className="text-xs text-white/50">{p.feeNote}</div>
    </div>
    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
      <SecondaryButton to={detailPath(p)} onDark className="flex-1" testId={`internships-view-${i}`}>View Details</SecondaryButton>
      <PrimaryButton to="/apply" className="flex-1" testId={`internships-apply-${i}`}>Apply Now</PrimaryButton>
    </div>
  </StaggerItem>
);

export default function Internships() {
  useSeo("Internships", "Free, selection-based ASAI internships in UPI and in AI & ML for selected candidates. Plus paid internship and industrial-training programs.");
  const internships = getByCategory("internship");

  return (
    <div data-testid="page-internships">
      <PageHeader
        overline="Internships"
        titleLines={["Learn by doing —", "real internships"]}
        description="Gain practical, hands-on experience. Our free internships are selection-based and open to selected candidates only."
      />

      <section className="bg-navy py-16 md:py-20">
        <div className="container-asai">
          <div className="mb-10 flex items-start gap-4 rounded-2xl border border-orange/30 bg-orange/10 p-6">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
            <p className="text-sm text-white/80">
              <span className="font-semibold text-white">Please note:</span> Free internships are
              absolutely free but available for <span className="font-semibold text-white">selected candidates only</span>,
              based on interest, participation, eligibility, seat availability and screening criteria.
            </p>
          </div>
          <StaggerGroup className="grid gap-6 md:grid-cols-2">
            {internships.map((p, i) => (
              <InternshipCard key={p.slug} p={p} i={i} />
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="container-asai flex flex-col items-start gap-8">
          <SectionHeading
            overline="Want more depth?"
            title="Explore our full programs"
            description="From a 3-month internship to a complete 1-year job-oriented program."
          />
          <div className="flex flex-wrap gap-3">
            <PrimaryButton to="/programs" withArrow testId="internships-see-programs">See Programs</PrimaryButton>
            <SecondaryButton to="/contact" testId="internships-counselling-btn">Talk to a Career Expert</SecondaryButton>
          </div>
        </div>
      </section>
    </div>
  );
}
